/**
 * Upload a definition file into the editor (LAR-1).
 *
 * **The file fills the editor; it is not a second input.** Trade's page kept the file and the
 * pasted text as separate state and hid the textarea once a file was chosen, which meant an author
 * who uploaded a portfolio and then spotted a typo had to fix it in their editor and upload again.
 * Reading the file into the text field makes the text the only source of truth: upload is how you
 * *fill* it, and everything after that is editing.
 *
 * It also removes the branch the old `handleAddSymphony` needed — it rebuilt a `File` out of pasted
 * text to satisfy a multipart endpoint. Lab's routes take JSON, so there is nothing to rebuild.
 */
import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';

interface Props {
  id: string;
  label: string;
  /** Passed to the input's `accept`, so the picker offers the right files first. */
  accept: string;
  disabled?: boolean;
  onLoad: (text: string) => void;
}

export function DefinitionFileInput({ id, label, accept, disabled, onLoad }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [chosen, setChosen] = useState<{ name: string; kb: string } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const read = async (file: File) => {
    setFailed(null);
    try {
      const text = await file.text();
      setChosen({ name: file.name, kb: (file.size / 1024).toFixed(2) });
      onLoad(text);
    } catch {
      // A directory, a permission refusal, or a file that vanished between picking and reading.
      setFailed(`Could not read ${file.name}.`);
      setChosen(null);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        disabled={disabled}
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) read(file);
          // Cleared so choosing the same file twice fires `change` again — after an edit, re-picking
          // the original file is how an author discards their changes.
          event.target.value = '';
        }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
      >
        <Upload className="h-4 w-4" aria-hidden="true" /> {label}
      </button>
      {chosen && (
        <span className="ml-2 text-xs text-slate-500">
          {chosen.name} ({chosen.kb} KB)
        </span>
      )}
      {failed && (
        <span role="alert" className="ml-2 text-xs text-red-700">
          {failed}
        </span>
      )}
    </div>
  );
}
